import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { PageContent } from '@components/pages'
import { PageContentHeader } from '@components/pages/page-content-header'
import { matchesPath } from '@shared/components/navigation'
import { equabotzSettingsPath, guestEquabotSettingsPath, Organization, User } from '@logic'
import { getOrganizations } from '@src/service'
import { withService, ServiceProps } from '@shared/hocs/with-service'
import { useGateway } from '../hooks/use-gateway'
import { ConnectionCard } from '../components/ConnectionCard'
import { GatewayOffline } from '../components/GatewayOffline'
import ConfigEditor from '../components/ConfigEditor'
import { JsonSchema } from '../types'
import { getInitialScopeIdFromSearch, isOrganizationScopeId } from '../services/scope-query'

type ScopeKind = 'personal' | 'organization'

interface Scope {
  kind: ScopeKind
  id: string
  label: string
  description: string
  /** True only when the current user has admin permission for this scope. */
  canEdit: boolean
  /** Organization reference when kind === 'organization'. */
  organization?: Organization
}

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  max-width: 100%;
  overflow-x: hidden;
`

const ScopeSelectorRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 0 16px;
`

const ScopeSelectorLabel = styled.label`
  color: ${(p) => p.theme.listDesc};
  font-size: 11px;
  font-family: 'NunitoSansSemiBold';
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const ScopeSelect = styled.select`
  padding: 10px 14px;
  border-radius: ${(p) => p.theme.radius};
  border: 1px solid ${(p) => p.theme.inputBorder ?? p.theme.inputBack};
  background-color: ${(p) => p.theme.inputBack};
  color: ${(p) => p.theme.white};
  font-size: 14px;
  font-family: 'NunitoSans';
  min-width: 280px;
  max-width: 480px;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${(p) => p.theme.accent};
  }
`

const ScopeSelectorHint = styled.span`
  color: ${(p) => p.theme.listDesc};
  font-size: 12px;
  font-family: 'NunitoSans';
`

interface OwnProps {
  /** User prop is injected by the route wrapper (see src/app/components/routes/routes.tsx). */
  user?: User
}

type Props = OwnProps & ServiceProps

function personalScope(user: User | undefined): Scope {
  return {
    kind: 'personal',
    id: 'personal',
    label: 'Personal account',
    description: user?.email
      ? `${user.email} — your personal Equa account (you are always the admin of your own profile)`
      : 'Your personal Equa account (you are always the admin of your own profile)',
    canEdit: true,
  }
}

function organizationScope(org: Organization, userId: string | undefined): Scope {
  const canEdit = !!userId && org.creator === userId
  return {
    kind: 'organization',
    id: `org:${org.id}`,
    label: org.name || 'Untitled organization',
    description: canEdit
      ? `${org.name} — you are the admin of this organization`
      : `${org.name} — member access only (admin required to edit)`,
    canEdit,
    organization: org,
  }
}

function EquabotSettingsPage(props: Props) {
  const { user, httpClient } = props

  const isGuestPath = matchesPath(guestEquabotSettingsPath, window.location.pathname, true)
  const gw = useGateway()

  // Load the user's organizations so we can build the scope list.
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [orgsLoading, setOrgsLoading] = useState(false)
  const [orgsLoaded, setOrgsLoaded] = useState(false)

  useEffect(() => {
    if (!user) {
      setOrganizations([])
      setOrgsLoaded(true)
      return
    }
    let cancelled = false
    setOrgsLoading(true)
    setOrgsLoaded(false)
    getOrganizations(httpClient)({})
      .then((resp: { organizations?: Organization[] }) => {
        if (!cancelled) setOrganizations(resp.organizations ?? [])
      })
      .catch(() => {
        if (!cancelled) setOrganizations([])
      })
      .finally(() => {
        if (!cancelled) {
          setOrgsLoading(false)
          setOrgsLoaded(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [user?.id, httpClient])

  // Build the list of scopes the user can pick from:
  //   1. Personal (always shown, always admin)
  //   2. Each organization where user is creator/admin
  //   3. Organizations where they are only a member are intentionally excluded
  //      — per product spec, only admins configure equabot settings.
  const scopes: Scope[] = useMemo(() => {
    const result: Scope[] = [personalScope(user)]
    for (const org of organizations) {
      const scope = organizationScope(org, user?.id)
      if (scope.canEdit) result.push(scope)
    }
    return result
  }, [user, organizations])

  const [selectedScopeId, setSelectedScopeId] = useState<string>(() =>
    getInitialScopeIdFromSearch(window.location.search)
  )

  // If the selected scope disappears (org list refetches and user is no longer
  // admin of that org), fall back to personal.
  useEffect(() => {
    if (isOrganizationScopeId(selectedScopeId) && user && !orgsLoaded) return
    if (!scopes.some((s) => s.id === selectedScopeId)) {
      setSelectedScopeId('personal')
    }
  }, [scopes, selectedScopeId, user, orgsLoaded])

  const selectedScope = scopes.find((s) => s.id === selectedScopeId) ?? scopes[0]

  const configLoading =
    gw.connectionStatus === 'connected' && (gw.config === null || gw.schema === null)

  const handleSave = async (patch: Record<string, unknown>) => {
    const ok = await gw.patchConfig(patch)
    if (!ok) {
      const msg = gw.errorMessage
      if (msg === 'config_conflict') {
        throw new Error('config changed since last load (INVALID_REQUEST)')
      }
      throw new Error(msg ?? 'Save failed')
    }
  }

  const showOffline =
    gw.connectionStatus === 'disconnected' || gw.connectionStatus === 'error'

  // Scope-based notice for the editor. Personal scope writes the local gateway
  // config directly. Org scope will eventually write an org-level config store,
  // but the gateway is single-tenant today — surface that honestly instead of
  // silently aliasing to the personal config.
  const scopeNotice =
    selectedScope?.kind === 'organization'
      ? 'Organization-scoped configuration is read-only until the gateway adds multi-tenant support. Edits shown below apply to the local (personal) gateway config for preview only.'
      : undefined

  const pageTitle =
    selectedScope?.kind === 'organization'
      ? `Equa Account Configuration — ${selectedScope.label}`
      : 'Equa Account Configuration'

  return (
    <PageContent withMargin={isGuestPath}>
      <PageContentHeader title={pageTitle} />

      <SettingsContainer>
        {/* Scope selector — personal + owned orgs. Hidden entirely if the user
            has no orgs, so the default single-scope experience is uncluttered. */}
        {scopes.length > 1 && (
          <ScopeSelectorRow>
            <ScopeSelectorLabel htmlFor="equabotz-scope-select">
              Configuring
            </ScopeSelectorLabel>
            <ScopeSelect
              id="equabotz-scope-select"
              value={selectedScopeId}
              onChange={(e) => setSelectedScopeId(e.target.value)}
              disabled={orgsLoading}
            >
              {scopes.map((scope) => (
                <option key={scope.id} value={scope.id}>
                  {scope.label}
                  {scope.kind === 'personal' ? ' (default)' : ''}
                </option>
              ))}
            </ScopeSelect>
            {selectedScope && (
              <ScopeSelectorHint>{selectedScope.description}</ScopeSelectorHint>
            )}
          </ScopeSelectorRow>
        )}

        <ConnectionCard
          connectionStatus={gw.connectionStatus}
          gatewayUrl={gw.gatewayUrl}
          token={gw.token}
          password={gw.password}
          errorMessage={gw.errorMessage}
          setGatewayUrl={gw.setGatewayUrl}
          setToken={gw.setToken}
          setPassword={gw.setPassword}
          connect={gw.connect}
          disconnect={gw.disconnect}
        />

        {showOffline && (
          <GatewayOffline
            onRetry={gw.connect}
            errorMessage={gw.errorMessage}
            hasToken={Boolean(gw.token.trim())}
          />
        )}

        {gw.connectionStatus === 'connected' && gw.config && gw.schema && gw.baseHash && (
          <ConfigEditor
            config={gw.config}
            schema={gw.schema as JsonSchema}
            uiHints={gw.uiHints ?? {}}
            baseHash={gw.baseHash}
            onSave={handleSave}
            onRefresh={gw.refreshConfig}
            loading={configLoading}
            scopeLabel={selectedScope?.label}
            scopeNotice={scopeNotice}
          />
        )}
      </SettingsContainer>
    </PageContent>
  )
}

export default withService(EquabotSettingsPage) as React.ComponentType<OwnProps>

// Reference canonical path for callers that need to link to this page.
// Import as: import { equabotzSettingsPath } from '@logic'
export { equabotzSettingsPath }
